import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Tooltip,
    IconButton,
    Card,
    Chip
} from '@mui/material';
import {
    DndContext,
    DragOverlay,
    closestCorners,
    pointerWithin,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Add as AddIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    LocationOn as LocationIcon,
    AccountTree as AccountTreeIcon,
    History as HistoryIcon,
    AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { updateTask, addTaskToQueue } from '../../../store/reducers/tasksReducer';
import { $authHost } from '../../../http';
import { useAuth } from 'react-oidc-context';
import { allowedTransitions, CreatedDateIcon, DeadlineDateIcon, EditedDateIcon, PersonCustomIcon, statusDefs } from '../TasksView';
import { Virtuoso } from 'react-virtuoso';
import { marked } from 'marked';

// Настройка marked для безопасной обработки markdown
marked.setOptions({
    breaks: true, // Переносы строк как <br>
    gfm: true, // GitHub Flavored Markdown
    sanitize: false, // Отключаем встроенную санитизацию (используем DOMPurify если нужно)
    smartLists: true,
    smartypants: false,
});

// Функция для безопасной обработки markdown
const processMarkdown = (text) => {
    if (!text) return '';

    try {
        return marked(text);
    } catch (error) {
        console.warn('Markdown parsing error:', error);
        // Возвращаем исходный текст с базовой обработкой переносов строк
        return text.replace(/\n/g, '<br>');
    }
};

// Оптимизированные стили с использованием CSS-in-JS для статических стилей
const styles = {
    container: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflowX: 'auto',
        overflowY: 'hidden',
        width: 'fit-content',

    },
    headerRow: {
        display: 'flex',
        gap: '0.25rem',
        padding: '0.25rem 0.5rem 0.1rem',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        marginBottom: '0rem',
        width: 'auto',
        // borderBottom: '1px solid',
    },
    contentRow: {
        display: 'flex',
        gap: '0.25rem',
        padding: '0.25rem 0.5rem 0.25rem',
        flex: 1,
        // overflowY: 'hidden',
        alignItems: 'flex-start',
        width: 'auto',
        height: '95%'
    },
    column: {
        borderRadius: '8px',
        padding: '0.2rem',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        transition: 'all 0.2s ease',
        flex: 1,
        minWidth: '200px',
        maxWidth: 'none',
        height: '100%',
        overflow: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    columnHeader: {
        padding: '0.25rem 1rem',
        borderRadius: '5px',
        fontWeight: 700,
        color: '#fff',
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.5rem',
        userSelect: 'none',
        cursor: 'pointer',
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        boxSizing: 'border-box',
        transition: 'all 0.2s ease',
    },
    tasksContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.2rem',
        height: '100%',
        minHeight: '200px',
        padding: '0',
        borderRadius: '4px',
        border: '1px dashed',
        position: 'relative',
        zIndex: 2,


    },
    taskCard: {
        borderRadius: '8px',
        padding: '0.25rem',
        fontSize: { xs: '0.775rem', md: '0.875rem' },
        fontWeight: 500,
        cursor: 'grab',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
    },
    taskName: {
        fontWeight: 700,
        fontSize: { xs: '0.85rem', md: '0.95rem' },
        lineHeight: 1.1,
        marginBottom: '0.25rem',
    },
    taskDesc: {
        fontSize: { xs: '0.75rem', md: '0.85rem' },
        // fontStyle: 'italic',
        lineHeight: 1.1,
        marginBottom: '0.5rem',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    metaContainer: {
        fontSize: { xs: '0.65rem', md: '0.75rem' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '0.4rem',
        padding: '0.5rem 0',
        borderTop: '1px solid',
        marginTop: '0rem',
    },
    metaRow: {
        display: 'flex',
        justifyContent: 'start',
        width: '100%',
        alignItems: 'center',
        gap: '0.3rem',
        fontSize: '0.75rem',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 0,
        pt: 0,
        px: 0.3,
        borderTop: '1px solid',
        userSelect: 'none'
    },
    buttonsLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.3
    }
};

// Мемоизированные компоненты для улучшения производительности
const MetaRowComponent = React.memo(({ icon: Icon, color, text, darkMode }) => (
    <Box
        sx={{
            ...styles.metaRow,
            color: darkMode ? '#999' : '#666',
            '& .MuiSvgIcon-root': {
                fontSize: '14px',
                opacity: 0.7
            },
        }}
    >
        {Icon && <Icon sx={{ fontSize: { xs: '12px', md: '16px' } }} color={color} />}
        <Typography variant="caption" sx={{
            color: color || 'inherit',
            fontSize: { xs: '0.65rem', md: '0.75rem' },
            fontWeight: 600,
            opacity: 1,
            lineHeight: 1.1
        }}>
            {text}
        </Typography>
    </Box>
));

// Оптимизированный компонент карточки задачи
const TaskCard = React.memo(({ task, users, isDragging, onEdit, onDelete, onViewSpecs, onViewFiles, onFocusArea, onHistory, darkMode, canEdit, canDrag, canDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging
    } = useSortable({
        id: task.id,
        data: {
            type: 'task',
            task
        }
    });

    const handleEdit = useCallback((e) => {
        e.stopPropagation();
        const taskToEdit = task.isOverdueCopy ?
            { ...task, id: task.originalId, status: task.originalStatus } :
            task;
        onEdit(taskToEdit);
    }, [task, onEdit]);

    const handleDelete = useCallback((e) => {
        e.stopPropagation();
        const taskIdToDelete = task.isOverdueCopy ? task.originalId : task.id;
        onDelete(taskIdToDelete);
    }, [task, onDelete]);

    const handleViewSpecs = useCallback((e) => {
        e.stopPropagation();
        onViewSpecs(task);
    }, [task, onViewSpecs]);

    const handleViewFiles = useCallback((e) => {
        e.stopPropagation();
        onViewFiles(task);
    }, [task, onViewFiles]);

    const handleFocusArea = useCallback((e) => {
        e.stopPropagation();
        onFocusArea(task);
    }, [task, onFocusArea]);

    const handleHistory = useCallback((e) => {
        e.stopPropagation();
        const taskForHistory = task.isOverdueCopy ?
            { ...task, id: task.originalId, status: task.originalStatus } :
            task;
        onHistory(taskForHistory);
    }, [task, onHistory]);

    // Мемоизируем стили для производительности
    const cardStyles = useMemo(() => ({
        ...styles.taskCard,
        backgroundColor: darkMode ? '#2b2b2b' : '#fff',
        color: darkMode ? '#f5f7fa' : '#333',
        borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
        boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
        opacity: isDragging || isSortableDragging ? 0.6 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        // Разрешаем вертикальный скролл до начала dnd на тач-устройствах
        touchAction: canDrag ? 'pan-y' : 'auto',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...(!canDrag && {
            opacity: 0.8,
            border: darkMode ? '1px dashed rgba(255,255,255,0.3)' : '1px dashed rgba(0,0,0,0.3)',
            cursor: 'default !important',
        })
    }), [darkMode, isDragging, isSortableDragging, transform, transition, canDrag]);

    const taskNameStyles = useMemo(() => ({
        ...styles.taskName,
        color: darkMode ? '#f5f7fa' : '#1a1a1a'
    }), [darkMode]);

    const taskDescStyles = useMemo(() => ({
        ...styles.taskDesc,
        color: darkMode ? '#bbb' : '#666'
    }), [darkMode]);

    const metaContainerStyles = useMemo(() => ({
        ...styles.metaContainer,
        color: darkMode ? '#999' : '#666',
        borderTopColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
    }), [darkMode]);

    const buttonContainerStyles = useMemo(() => ({
        ...styles.buttonContainer,
        borderTopColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
    }), [darkMode]);

    // Обработка HTML контента задач с мемоизацией
    const processedDescription = useMemo(() => {
        const description = task.description || task.desc;
        return processMarkdown(description);
    }, [task.description, task.desc]);

    const isOverdue = useMemo(() => {
        return task.deadline && new Date() > new Date(task.deadline) && task.status !== 'done' && task.status !== 'cancelled';
    }, [task.deadline]);

    return (
        <Card
            ref={setNodeRef}
            sx={cardStyles}
            {...(canDrag ? attributes : {})}
            {...(canDrag ? listeners : {})}
        >
            {/* Заголовок и индикатор просроченности */}
            <Box sx={{ userSelect: 'none' }}>
                <Typography sx={taskNameStyles}>
                    {task.title || task.name}
                </Typography>
                {task.isOverdueCopy && (
                    <Chip
                        label="Копия просроченной"
                        size="small"
                        sx={{
                            height: { xs: 14, md: 16 },
                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            color: '#f44336',
                            border: '1px solid rgba(244, 67, 54, 0.3)',
                            mb: { xs: 0.3, md: 0.5 }
                        }}
                    />
                )}
            </Box>

            {/* Контент задачи */}
            <Box sx={{ cursor: canDrag ? 'grab' : 'default', userSelect: 'none', flex: 1 }}>
                {processedDescription && (
                    <Tooltip disableTouchListener title={
                        <Typography sx={{ fontSize: '0.75rem', p: 0.5, lineHeight: 1.1, '& p': { margin: 0 } }} dangerouslySetInnerHTML={{ __html: processedDescription }} />
                    }>
                        <Typography
                            sx={{
                                ...taskDescStyles,
                                '& p': {
                                    margin: 0
                                },
                            }}
                            dangerouslySetInnerHTML={{ __html: processedDescription }}
                        />
                    </Tooltip>
                )}
                <Box sx={metaContainerStyles}>
                    {/* Группа 1: Создатель и дата создания */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<PersonCustomIcon color={darkMode ? '#98DB51' : '#7CB342'} sx={{ fontSize: '14px' }} />}
                            label={users.get(task.creator)}
                            size="small"
                            sx={{
                                backgroundColor: darkMode ? 'rgba(124, 179, 66, 0.15)' : 'rgba(124, 179, 66, 0.1)',
                                color: darkMode ? '#98DB51' : '#7CB342',
                                border: '1px solid rgba(124, 179, 66, 0.3)',
                                '& .MuiChip-icon': {
                                    color: darkMode ? '#98DB51' : '#7CB342'
                                },
                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                height: { xs: '16px', md: '20px' }
                            }}
                        />
                        <Chip
                            icon={<CreatedDateIcon color={darkMode ? '#98DB51' : '#7CB342'} sx={{ fontSize: '14px' }} />}
                            label={formatDate(new Date(task.created_at))}
                            size="small"
                            sx={{
                                backgroundColor: darkMode ? 'rgba(124, 179, 66, 0.15)' : 'rgba(124, 179, 66, 0.1)',
                                color: darkMode ? '#98DB51' : '#7CB342',
                                border: '1px solid rgba(124, 179, 66, 0.3)',
                                '& .MuiChip-icon': {
                                    color: darkMode ? '#98DB51' : '#7CB342'
                                },
                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                height: { xs: '16px', md: '20px' }
                            }}
                        />
                    </Box>

                    {/* Группа 2: Ответственный и дедлайн */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<PersonCustomIcon color={isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2'} sx={{ fontSize: '14px' }} />}
                            label={users.get(task.responsible)}
                            size="small"
                            sx={{
                                backgroundColor: isOverdue
                                    ? (darkMode ? 'rgba(220, 20, 60, 0.15)' : 'rgba(220, 20, 60, 0.1)')
                                    : (darkMode ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.1)'),
                                color: isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2',
                                border: `1px solid ${isOverdue ? 'rgba(220, 20, 60, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                                '& .MuiChip-icon': {
                                    color: isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2'
                                },
                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                height: { xs: '16px', md: '20px' }
                            }}
                        />
                        <Chip
                            icon={<DeadlineDateIcon color={isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2'} sx={{ fontSize: '14px' }} />}
                            label={formatDate(new Date(task.deadline))}
                            size="small"
                            sx={{
                                backgroundColor: isOverdue
                                    ? (darkMode ? 'rgba(220, 20, 60, 0.15)' : 'rgba(220, 20, 60, 0.1)')
                                    : (darkMode ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.1)'),
                                color: isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2',
                                border: `1px solid ${isOverdue ? 'rgba(220, 20, 60, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                                '& .MuiChip-icon': {
                                    color: isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2'
                                },
                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                height: { xs: '16px', md: '20px' }
                            }}
                        />
                        {/* Чип для соисполнителей (показывается только если есть соисполнители) */}
                        {task.coexecutors && task.coexecutors.length > 0 && (
                            task.coexecutors.map(c => (
                                <Chip
                                    key={c}
                                    icon={<PersonCustomIcon color={darkMode ? '#CB33E6' : '#9C27B0'} sx={{ fontSize: '14px' }} />}
                                    label={users.get(c)}
                                    size="small"
                                    sx={{
                                        backgroundColor: darkMode ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#CB33E6' : '#9C27B0',
                                        border: '1px solid rgba(156, 39, 176, 0.3)',
                                        '& .MuiChip-icon': {
                                            color: darkMode ? '#CB33E6' : '#9C27B0'
                                        },
                                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                                        height: { xs: '16px', md: '20px' }
                                    }}
                                />
                            ))

                        )}
                    </Box>

                    {/* Группа 3: Редактор и дата изменения (показывается только если есть данные) */}
                    {task.editor && task.edited_at && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                                icon={<PersonCustomIcon color={darkMode ? '#7ED1D4' : '#5F9EA0'} sx={{ fontSize: '14px' }} />}
                                label={users.get(task.editor)}
                                size="small"
                                sx={{
                                    backgroundColor: darkMode ? 'rgba(95, 158, 160, 0.15)' : 'rgba(95, 158, 160, 0.1)',
                                    color: darkMode ? '#7ED1D4' : '#5F9EA0',
                                    border: '1px solid rgba(95, 158, 160, 0.3)',
                                    '& .MuiChip-icon': {
                                        color: darkMode ? '#7ED1D4' : '#5F9EA0'
                                    },
                                    fontSize: { xs: '0.65rem', md: '0.7rem' },
                                    height: { xs: '16px', md: '20px' }
                                }}
                            />
                            <Chip
                                icon={<EditedDateIcon color={darkMode ? '#7ED1D4' : '#5F9EA0'} sx={{ fontSize: '14px' }} />}
                                label={formatDate(new Date(task.edited_at))}
                                size="small"
                                sx={{
                                    backgroundColor: darkMode ? 'rgba(95, 158, 160, 0.15)' : 'rgba(95, 158, 160, 0.1)',
                                    color: darkMode ? '#7ED1D4' : '#5F9EA0',
                                    border: '1px solid rgba(95, 158, 160, 0.3)',
                                    '& .MuiChip-icon': {
                                        color: darkMode ? '#7ED1D4' : '#5F9EA0'
                                    },
                                    fontSize: { xs: '0.65rem', md: '0.7rem' },
                                    height: { xs: '16px', md: '20px' }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Нижняя панель с кнопками и ID */}
            <Box sx={buttonContainerStyles}>
                <Box sx={styles.buttonsLeft}>
                    {/* Кнопка наведения на область карты */}
                    {task.data?.objects && task.data.objects.pillars && task.data.objects.pillars.length > 0 && (
                        <IconButton
                            size="small"
                            onClick={handleFocusArea}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                color: darkMode ? '#4fc3f7' : '#0277bd',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: darkMode ? 'rgba(79, 195, 247, 0.08)' : 'rgba(2, 119, 189, 0.04)'
                                }
                            }}
                            title="Показать на карте"
                        >
                            <LocationIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}

                    {/* Кнопка просмотра материалов */}
                    {(task.type === 'contour' || task.type === 'points') && Object.keys(task.data.objects).some(type => task.data.objects[type]?.length > 0) && (
                        <IconButton
                            size="small"
                            onClick={handleViewSpecs}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                color: darkMode ? '#ce93d8' : '#9c27b0',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: darkMode ? 'rgba(156, 39, 176, 0.08)' : 'rgba(156, 39, 176, 0.04)'
                                }
                            }}
                        >
                            <AccountTreeIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}

                    {/* Кнопка просмотра файлов */}
                    {/* {task.files && task.files.length > 0 && ( */}
                    {!task.isOverdueCopy && (
                        <IconButton
                            size="small"
                            onClick={handleViewFiles}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                color: darkMode ? '#81c784' : '#4caf50',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: darkMode ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.04)'
                                }
                            }}
                        >
                            <AttachFileIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}

                    {canEdit && !task.isOverdueCopy && (
                        <IconButton
                            size="small"
                            onClick={handleEdit}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                color: darkMode ? '#ddd' : '#333',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <EditIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}

                    {/* Кнопка истории изменений */}
                    {!task.isOverdueCopy && (
                        <IconButton
                            size="small"
                            onClick={handleHistory}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                color: darkMode ? '#ff9800' : '#f57c00',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(245, 124, 0, 0.1)',
                                }
                            }}
                        >
                            <HistoryIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}

                    {canDelete && !task.isOverdueCopy && (
                        <IconButton
                            size="small"
                            onClick={handleDelete}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                opacity: 0.8,
                                color: 'rgba(244, 67, 54, 1)',
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: 'rgba(244, 67, 54, 0.08)'
                                }
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}
                </Box>

                <Chip
                    label={task.isOverdueCopy ? task.originalId : task.id}
                    size="small"
                    sx={{
                        backgroundColor: darkMode ? 'rgba(137, 164, 165, 0.15)' : 'rgba(132, 157, 158, 0.1)',
                        color: darkMode ? '#888' : '#666',
                        border: '1px solid rgba(121, 155, 156, 0.3)',
                        '& .MuiChip-icon': {
                            color: darkMode ? '#888' : '#666'
                        },
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        height: { xs: '16px', md: '20px' }
                    }}
                />
                {/* <Typography
                    variant="caption"
                    sx={{
                        color: darkMode ? '#888' : '#666',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        opacity: 0.9
                    }}
                >
                    id {task.isOverdueCopy ? task.originalId : task.id}
                </Typography> */}
            </Box>
        </Card>
    );
});

// Оптимизированный компонент колонки с виртуализацией
const VirtualizedColumn = React.memo(({ status, tasks, users, darkMode, draggedTask, onEdit, onDelete, onViewSpecs, onViewFiles, onFocusArea, onHistory, canEditTask, canDragTask, canDeleteTask, isCollapsed }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: status.id,
        data: {
            type: 'column',
            status: status.id
        }
    });

    // Определяем статус исходной колонки из перетаскиваемой задачи
    const draggedTaskSourceStatus = draggedTask
        ? (draggedTask.isOverdueCopy ? draggedTask.originalStatus : draggedTask.status)
        : null;

    // Проверяем, можно ли перетащить задачу в эту колонку
    const isDropAllowed = !draggedTaskSourceStatus ||
        (allowedTransitions[draggedTaskSourceStatus]?.includes(status.id) &&
            !noDropZones.includes(status.id));

    // Мемоизируем стили колонки для производительности
    const columnStyles = useMemo(() => {
        const baseStyles = {
            ...styles.column,
            borderColor: darkMode ? `${status.color}40` : `${status.color}30`,
            boxShadow: darkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
        };

        if (!draggedTaskSourceStatus) {
            return {
                ...baseStyles,
                backgroundColor: darkMode ? `${status.color}20` : `${status.color}15`,
                opacity: 1,
                filter: 'none'
            };
        }

        if (!isDropAllowed) {
            return {
                ...baseStyles,
                backgroundColor: darkMode ? 'rgba(128, 128, 128, 0.15)' : 'rgba(128, 128, 128, 0.1)',
                opacity: 0.4,
                filter: 'grayscale(70%)',
                cursor: 'not-allowed'
            };
        }

        if (isOver) {
            return {
                ...baseStyles,
                backgroundColor: darkMode ? `${status.color}50` : `${status.color}40`,
                opacity: 1,
                filter: 'brightness(1.1)',
                borderColor: status.color,
                borderWidth: '2px',
                transform: 'scale(1.02)'
            };
        }

        return {
            ...baseStyles,
            backgroundColor: darkMode ? `${status.color}25` : `${status.color}20`,
            opacity: 1,
            filter: 'brightness(1.05)'
        };
    }, [status, darkMode, draggedTaskSourceStatus, isDropAllowed, isOver]);

    const tasksContainerStyles = useMemo(() => ({
        ...styles.tasksContainer,
        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.5)',
        borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        minHeight: tasks.length > 10 ? '400px' : '200px' // Динамическая высота для оптимизации
    }), [darkMode, tasks.length]);

    // Компонент элемента виртуального списка
    const TaskItem = useCallback(({ index }) => {
        const task = tasks[index];
        return (
            <Box sx={{ padding: '0.25rem 0' }}>
                <TaskCard
                    task={task}
                    users={users}
                    isDragging={draggedTask?.id === task.id}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onViewSpecs={onViewSpecs}
                    onViewFiles={onViewFiles}
                    onFocusArea={onFocusArea}
                    onHistory={onHistory}
                    darkMode={darkMode}
                    canEdit={canEditTask(task)}
                    canDrag={canDragTask(task, status.id)}
                    canDelete={canDeleteTask(task)}
                />
            </Box>
        );
    }, [tasks, draggedTask, onEdit, onDelete, onViewSpecs, onFocusArea, onHistory, darkMode, canEditTask, canDragTask, canDeleteTask, status.id]);

    if (isCollapsed) {
        return (
            <Box sx={{ ...columnStyles, flex: '0 0 35px', minWidth: '35px', padding: '0.2rem 0', overflow: 'hidden' }}>
                <Box
                    ref={setNodeRef}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1,
                        pointerEvents: draggedTaskSourceStatus ? 'auto' : 'none'
                    }}
                />
            </Box>
        );
    }

    return (
        <Box sx={columnStyles}>
            {/* Невидимая droppable зона */}
            <Box
                ref={setNodeRef}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                    pointerEvents: draggedTaskSourceStatus ? 'auto' : 'none'
                }}
            />

            <Box sx={tasksContainerStyles}>
                <SortableContext
                    items={tasks.map(task => task.id)}
                    strategy={verticalListSortingStrategy}

                >
                    {tasks.length > 10 ? (
                        // Виртуализация для больших списков
                        <Virtuoso
                            style={{
                                height: '100%', scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                '&::WebkitScrollbar': {
                                    display: 'none',
                                },
                            }}
                            data={tasks}
                            itemContent={(index) => <TaskItem index={index} />}
                            overscan={5} // Оптимизация для плавной прокрутки
                        />
                    ) : (
                        // Обычный рендеринг для небольших списков
                        tasks.map((task, index) => <TaskItem key={task.id} index={index} />)
                    )}
                </SortableContext>
            </Box>
        </Box>
    );
});

// Оптимизированный DragOverlay компонент
const OptimizedDragOverlay = React.memo(({ draggedTask, darkMode }) => {
    const processedDescription = useMemo(() => {
        if (!draggedTask) return '';
        const description = draggedTask.description || draggedTask.desc;
        return processMarkdown(description);
    }, [draggedTask?.description, draggedTask?.desc]);

    if (!draggedTask) return null;

    return (
        <div role="none" aria-hidden="true" style={{ pointerEvents: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}>
            <Card sx={{
                ...styles.taskCard,
                backgroundColor: darkMode ? '#2b2b2b' : '#fff',
                color: darkMode ? '#f5f7fa' : '#333',
                borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                opacity: 0.8,
                transform: 'rotate(5deg)',
                pointerEvents: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                '& *': { pointerEvents: 'none', tabIndex: -1, userSelect: 'none', WebkitUserSelect: 'none' }
            }}>
                <Typography sx={{
                    ...styles.taskName,
                    color: darkMode ? '#f5f7fa' : '#1a1a1a'
                }}>
                    {draggedTask.title || draggedTask.name}
                </Typography>

                {processedDescription && (
                    <Typography
                        sx={{
                            ...styles.taskDesc,
                            color: darkMode ? '#bbb' : '#666',
                            '& strong, & b': {
                                fontWeight: 700,
                                color: darkMode ? '#fff' : '#1a1a1a'
                            },
                            '& em, & i': {
                                fontStyle: 'italic',
                                color: darkMode ? '#e0e0e0' : '#424242'
                            },
                            '& ul, & ol': {
                                paddingLeft: 2,
                                margin: 0,
                                color: darkMode ? '#bbb' : '#666'
                            },
                            '& li': {
                                listStyle: 'disc',
                                marginBottom: '2px',
                                lineHeight: 1.2
                            },
                            '& code': {
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                padding: '0.1rem 0.2rem',
                                borderRadius: '0.2rem',
                                fontFamily: 'monospace',
                                fontSize: '0.8em',
                                color: darkMode ? '#ff6b6b' : '#d63384'
                            },
                            flex: 1,
                            pointerEvents: 'none'
                        }}
                        dangerouslySetInnerHTML={{ __html: processedDescription }}
                    />
                )}

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 1,
                    pt: 1,
                    borderTop: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                    pointerEvents: 'none'
                }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: darkMode ? '#888' : '#666',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            fontFamily: 'monospace',
                            opacity: 0.8,
                            pointerEvents: 'none'
                        }}
                    >
                        #{draggedTask.isOverdueCopy ? draggedTask.originalId : draggedTask.id}
                    </Typography>
                </Box>
            </Card>
        </div>
    );
});

// Утилитарные функции
const formatDate = (date) => {
    if (!date) return 'Не указана';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Неверная дата';

    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${hours}:${minutes} ${day}.${month}.${year}`;
};

const noDropZones = ['overdue'];



// Основной компонент KanbanView2
const KanbanView2 = ({
    darkMode,
    isOverdueCollapsed,
    setIsOverdueCollapsed,
    canCreateTasks,
    openCreateTaskDialog,
    tasks,
    users,
    handleEditTask,
    handleDeleteTask,
    handleViewSpecs,
    handleViewFiles,
    handleFocusOnTaskArea,
    handleOpenHistoryDialog,
    canEditTask,
    canDragTask,
    canDeleteTask
}) => {
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.state.user);
    const auth = useAuth();

    // Состояние drag and drop
    const [draggedTask, setDraggedTask] = useState(null);

    // Мемоизированные сенсоры
    const isCoarsePointer = useMemo(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        try {
            return window.matchMedia('(pointer: coarse)').matches;
        } catch (e) {
            return false;
        }
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: isCoarsePointer
                ? { delay: 180, tolerance: 8 }
                : { distance: 6 }
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 180, tolerance: 8 }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    // Оптимизированный алгоритм обнаружения коллизий
    const customCollisionDetection = useCallback((args) => {
        const columnCollisions = pointerWithin({
            ...args,
            droppableContainers: args.droppableContainers.filter(
                container => container.data.current?.type === 'column'
            )
        });

        return columnCollisions.length > 0 ? columnCollisions : closestCorners(args);
    }, []);

    // Мемоизированные обработчики событий
    const handleDragStart = useCallback((event) => {
        const { active } = event;
        const task = active.data.current?.task;
        if (task) {
            setDraggedTask(task);
        }
    }, []);

    const handleDragCancel = useCallback(() => {
        setDraggedTask(null);
    }, []);

    const handleDragEnd = useCallback(async (event) => {
        const { active, over } = event;
        setDraggedTask(null);

        if (!over) return;

        const activeTask = active.data.current?.task;
        const overColumn = over.data.current?.status;

        if (!activeTask || !overColumn || activeTask.isOverdueCopy) return;

        const sourceStatus = activeTask.status;

        if (!canDragTask(activeTask) ||
            !allowedTransitions[sourceStatus]?.includes(overColumn) ||
            sourceStatus === overColumn ||
            noDropZones.includes(overColumn)) {
            return;
        }

        const updatedTask = {
            ...activeTask,
            status: overColumn,
            editor: currentUser.id
        };

        dispatch(updateTask(updatedTask));



        if (auth.isAuthenticated && auth.user?.access_token && !activeTask.isLocal) {
            try {
                const response = await $authHost.put(`/apig/tasks/${activeTask.id}`, updatedTask);

                if (!response.data.success) {
                    throw new Error('Server returned unsuccessful response');
                }
            } catch (err) {
                console.error('Ошибка синхронизации статуса:', err);
                dispatch(addTaskToQueue({
                    action: 'update',
                    task: updatedTask,
                    originalTaskId: activeTask.id,
                    timestamp: Date.now()
                }));
            }
        } else {
            dispatch(addTaskToQueue({
                action: activeTask.isLocal ? 'create' : 'update',
                task: updatedTask,
                originalTaskId: activeTask.isLocal ? null : activeTask.id,
                timestamp: Date.now()
            }));
        }
    }, [canDragTask, currentUser.id, dispatch, auth, allowedTransitions]);

    // Мемоизированные стили для контейнеров
    const containerStyles = useMemo(() => ({
        ...styles.container,
        '&::-webkit-scrollbar': {
            height: '8px'
        },
        '&::-webkit-scrollbar-track': {
            background: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
            background: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)'
        },
        backgroundColor: darkMode ? '#1a1a1a' : '#f8fafc'
    }), [darkMode]);

    const headerRowStyles = useMemo(() => ({
        ...styles.headerRow,
        backgroundColor: darkMode ? '#1a1a1a' : '#f8fafc',
        borderBottomColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
    }), [darkMode]);

    const contentRowStyles = useMemo(() => ({
        ...styles.contentRow,
        '&::-webkit-scrollbar-track': {
            background: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
            background: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)'
        }
    }), [darkMode]);

    // Мемоизированные обработчики для заголовков
    const handleHeaderClick = useCallback((statusId) => {
        if (statusId === 'overdue') {
            setIsOverdueCollapsed(!isOverdueCollapsed);
        } else if (statusId === "new" && canCreateTasks) {
            openCreateTaskDialog();
        }
    }, [isOverdueCollapsed, setIsOverdueCollapsed, canCreateTasks, openCreateTaskDialog]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <Box sx={containerStyles}>
                {/* Закрепленные заголовки колонок */}
                <Box sx={headerRowStyles}>
                    {statusDefs.map(status => (
                        <Box key={`header-${status.id}`} sx={{
                            flex: status.id === 'overdue' && isOverdueCollapsed ? '0 0 35px' : 1,
                            minWidth: status.id === 'overdue' && isOverdueCollapsed ? '35px' : '200px',
                            maxWidth: 'none',
                            boxSizing: 'border-box',
                            padding: { xs: '0.1rem', md: '0.2rem' },
                            transition: 'all 0.3s ease',
                            flexShrink: 0,
                        }}>
                            <Box
                                sx={{
                                    ...styles.columnHeader,
                                    backgroundColor: status.color,
                                    boxShadow: darkMode ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
                                    opacity: draggedTask &&
                                        !allowedTransitions[draggedTask.isOverdueCopy ? draggedTask.originalStatus : draggedTask.status]?.includes(status.id)
                                        ? 0.5 : 1,
                                    filter: draggedTask &&
                                        !allowedTransitions[draggedTask.isOverdueCopy ? draggedTask.originalStatus : draggedTask.status]?.includes(status.id)
                                        ? 'grayscale(50%)' : 'none',
                                    cursor: (status.id === "new" && canCreateTasks) || status.id === 'overdue' ? 'pointer' : 'default',
                                    ...(status.id === 'overdue' && {
                                        background: `linear-gradient(135deg, ${status.color}, ${status.color}CC)`,
                                        position: 'relative',
                                    }),
                                    ...(status.id === 'overdue' && isOverdueCollapsed && {
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '100%',
                                    }),
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: darkMode ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 8px rgba(0, 0, 0, 0.3)',
                                        filter: 'brightness(1.1)'
                                    }
                                }}
                                onClick={() => handleHeaderClick(status.id)}
                            >
                                {status.id === 'overdue' ? (
                                    isOverdueCollapsed ? (
                                        <Tooltip title="Развернуть">
                                            <VisibilityIcon sx={{ fontSize: { xs: 16, md: 18 }, opacity: 0.8 }} />
                                        </Tooltip>
                                    ) : (
                                        <>
                                            <Typography variant="body2" sx={{ color: 'inherit', fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                                                {status.title} - {tasks[status.id]?.length || 0}
                                            </Typography>

                                            <Tooltip title="Свернуть">
                                                <VisibilityOffIcon sx={{ fontSize: { xs: 16, md: 18 }, opacity: 0.8, ml: 0.5 }} />
                                            </Tooltip>
                                        </>
                                    )
                                ) : (
                                    <>
                                        {status.id === "new" && canCreateTasks && <AddIcon sx={{ fontSize: { xs: 16, md: 20 } }} />}
                                        <Typography variant="body2" sx={{ color: 'inherit', fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                                            {status.title} - {tasks[status.id]?.length || 0}
                                        </Typography>

                                    </>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Контент колонок */}
                <Box sx={contentRowStyles}>
                    {statusDefs.map(status => (
                        <VirtualizedColumn
                            key={status.id}
                            status={status}
                            tasks={tasks[status.id] || []}
                            users={users}
                            darkMode={darkMode}
                            draggedTask={draggedTask}
                            onEdit={handleEditTask}
                            onDelete={handleDeleteTask}
                            onViewSpecs={handleViewSpecs}
                            onViewFiles={handleViewFiles}
                            onFocusArea={handleFocusOnTaskArea}
                            onHistory={handleOpenHistoryDialog}
                            canEditTask={canEditTask}
                            canDragTask={canDragTask}
                            canDeleteTask={canDeleteTask}
                            isCollapsed={status.id === 'overdue' && isOverdueCollapsed}
                        />
                    ))}
                </Box>
            </Box>

            <DragOverlay dropAnimation={null}>
                <OptimizedDragOverlay draggedTask={draggedTask} darkMode={darkMode} />
            </DragOverlay>
        </DndContext>
    );
};

export default KanbanView2;
